import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const DURATION = { success: 4000, info: 4000, error: 8000 };
const MAX_VISIBLE = 4;

const ICONS = {
  success: <CircleCheck size={17} strokeWidth={2} className="shrink-0 mt-px text-ok" aria-hidden="true" />,
  error: <CircleAlert size={17} strokeWidth={2} className="shrink-0 mt-px text-crit" aria-hidden="true" />,
  info: <Info size={17} strokeWidth={2} className="shrink-0 mt-px text-signal" aria-hidden="true" />,
};

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((toast) => toast.id !== id)), []);

  const show = useCallback(
    (tone, message) => {
      const id = nextId;
      nextId += 1;
      setToasts((list) => [...list.slice(-(MAX_VISIBLE - 1)), { id, tone, message }]);
      window.setTimeout(() => dismiss(id), DURATION[tone]);
      return id;
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      success: (message) => show('success', message),
      error: (message) => show('error', message),
      info: (message) => show('info', message),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div aria-live="polite" className="fixed bottom-5 right-5 z-[3500] flex flex-col items-end gap-2 pointer-events-none">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              role={toast.tone === 'error' ? 'alert' : 'status'}
              className="toast-in pointer-events-auto flex items-start gap-2.5 w-[360px] max-w-[calc(100vw-40px)] pl-3.5 pr-1.5 py-2.5 bg-sheet border border-rule rounded-panel shadow-overlay text-body text-ink"
            >
              {ICONS[toast.tone]}
              <p className="flex-1 min-w-0 break-words">{toast.message}</p>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="shrink-0 -my-0.5 p-1 rounded-control text-graphite hover:bg-hover hover:text-ink"
                aria-label="Dismiss notification"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
}
